import { Controller } from "@hotwired/stimulus"

// Adapts the inline line-item form to the selected "Art". For a Nachlass / Gutschrift
// (position_type = discount) Menge and Einheit carry no meaning, so they are hidden AND
// disabled — hiding alone would still post them, and a disabled field is what tells an
// assistive technology that it is out of play.
//
// The amount and the percentage are alternatives: typing in one clears the other, so the
// form never posts a Nachlass whose two halves disagree. The server decides the same way
// (Billing::LineItem#normalize_discount_line lets the percentage win); this only makes it
// visible while typing.
//
// The Art select is a SlimSelect (data-controller="select"); SlimSelect writes through to
// the original <select> and dispatches "change" on it, so listening on the element works.
export default class extends Controller {
  static targets = [
    "typeSelect",
    "quantityField",
    "unitField",
    "amountInput",
    "amountLabel",
    "percentInput",
    "hint",
    "costTypeField",
  ]
  static values = { discount: String }

  connect() {
    this.apply()
  }

  // The "Art" was switched. A Positionsrabatt already in the percent field means something
  // else than a Nachlass percentage: left standing, the server reads it as a percentage of
  // the Netto-Zwischensumme (the percentage wins in
  // Billing::LineItem#normalize_discount_line) and discards whatever was typed as the
  // amount. Only on an actual switch, never on connect() — an existing percentage Nachlass
  // must keep its value when its form is reopened.
  typeChanged() {
    if (this.discountSelected && this.hasPercentInputTarget) this.percentInputTarget.value = ""
    this.apply()
  }

  apply() {
    const discount = this.discountSelected

    // The controller element wraps the field row AND the hint, so it also connects on a
    // Titel's form — where Menge and Einheit do not exist at all. Without the guards
    // Stimulus would throw "Missing target element" every time a Titel form is opened.
    if (this.hasQuantityFieldTarget) this.toggleField(this.quantityFieldTarget, discount)
    if (this.hasUnitFieldTarget) this.toggleField(this.unitFieldTarget, discount)

    if (this.hasHintTarget) this.hintTarget.classList.toggle("hidden", !discount)

    // Die Kostenart nach § 35a EStG trägt ein Nachlass nicht (Billing::LaborCostSplit
    // lässt Nachlasszeilen als Gewicht aussen vor). Nur ausgeblendet, nicht deaktiviert:
    // Billing::LineItem#normalize_discount_line verwirft cost_type und
    // labor_share_percent ohnehin serverseitig, und das Prozentfeld darin gehört dem
    // billing-cost-type-Controller — würden beide es an- und abschalten, hinge sein
    // disabled-Zustand an der Reihenfolge, in der die beiden gerade laufen.
    if (this.hasCostTypeFieldTarget) this.costTypeFieldTarget.classList.toggle("hidden", discount)

    if (this.hasAmountLabelTarget) {
      const { discountLabel, defaultLabel } = this.amountLabelTarget.dataset
      this.amountLabelTarget.textContent = discount ? discountLabel : defaultLabel
    }
  }

  amountEntered() {
    if (this.discountSelected && this.amountInputTarget.value !== "")
      this.percentInputTarget.value = ""
  }

  percentEntered() {
    if (this.discountSelected && this.percentInputTarget.value !== "")
      this.amountInputTarget.value = ""
  }

  get discountSelected() {
    return this.hasTypeSelectTarget && this.typeSelectTarget.value === this.discountValue
  }

  // SlimSelect renders its own markup next to the original <select>, so the wrapper is
  // hidden rather than the input: that takes the visible control with it.
  toggleField(field, hidden) {
    field.classList.toggle("hidden", hidden)
    field.querySelectorAll("input, select").forEach((input) => {
      input.disabled = hidden
    })
  }
}
